import type {CSSProperties} from 'react';
import {duration, font, radius, space, weight} from '../scale.js';
import {wedgeExactRole, wedgeRole} from './wedgeRoles.js';

export type WedgeFollowUpLook = 'follow-up-snooze' | 'follow-up-done' | 'follow-up-open' | 'follow-up-menu-toggle' | 'follow-up-menu-toggle-open' | 'follow-up-menu-action' | 'follow-up-menu-done';

/* Released follow-up row of Today's Quick triage: two swipe action cells, the open button of the row, the chevron toggle of the
   menu (closed and open) and the menu actions (plain and Done). A cell is 78px wide with a centered column, a 4px gap and 11px
   bold type; Snooze sits on the amber fill and Done on the accent fill. The open button takes the free width of the row with an
   unclipped minimum. The toggle is a 30px box with an 8px radius that turns 180 degrees when open and takes the elevated fill; its
   turn runs 160ms on the legacy ease. A menu action is a 10px radius block with off-scale 9px by 8px padding and 13px bold type,
   the elevated fill, or the accent fill for Done. Each reads the exact legacy roles. The caller owns the children, the labels and
   the handlers. The Snooze label reads --on-accent and falls back to the on-accent token, where the source fell back to white; the
   wedge scope always sets --on-accent, so the fallback never applies there. */
const CELL_WIDTH = 78, TOGGLE_BOX = 30, MENU_PY = 9, MENU_PX = 8;

export function wedgeFollowUpStyle(look: WedgeFollowUpLook): CSSProperties {
  if (look === 'follow-up-snooze' || look === 'follow-up-done') {
    const snooze = look === 'follow-up-snooze';
    return {
      width: CELL_WIDTH, background: snooze ? wedgeExactRole('amber') : wedgeExactRole('accent'),
      color: snooze ? wedgeRole('onAccent') : wedgeExactRole('onAccent'), border: 0, cursor: 'pointer',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: space[1],
      fontSize: font.micro, fontWeight: weight.bold, fontFamily: wedgeExactRole('font'),
    };
  }
  if (look === 'follow-up-open') return {
    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: space[3], background: 'none', border: 0, padding: 0,
    textAlign: 'left', cursor: 'pointer', fontFamily: wedgeExactRole('font'),
  };
  if (look === 'follow-up-menu-toggle' || look === 'follow-up-menu-toggle-open') {
    const open = look === 'follow-up-menu-toggle-open';
    return {
      flexShrink: 0, width: TOGGLE_BOX, height: TOGGLE_BOX, borderRadius: radius.sm,
      background: open ? wedgeExactRole('bgElev') : 'none', border: 0, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: wedgeExactRole('labelTer'),
      transform: open ? 'rotate(180deg)' : 'none', transition: `transform ${duration.base}ms ${wedgeExactRole('ease')}`,
    };
  }
  const done = look === 'follow-up-menu-done';
  return {
    flex: '1 1 auto', padding: `${MENU_PY}px ${MENU_PX}px`, borderRadius: radius.md,
    background: done ? wedgeExactRole('accent') : wedgeExactRole('bgElev'),
    color: done ? wedgeExactRole('onAccent') : wedgeExactRole('label'), border: 0, cursor: 'pointer',
    fontFamily: wedgeExactRole('font'), fontSize: font.footnote, fontWeight: weight.bold,
  };
}
